import { useTranslation } from "react-i18next";
import React, { useState } from "react";
import { CheckCircle2, FileText, Loader2, X } from "lucide-react";
import { apiClient, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const isPdf = (url) => /\.pdf(\?|$)/i.test(url || "");

/** Miniature cliquable d'un document image (lightbox), PDF (icône cliquable), ou "Non fourni". */
function DocThumb({ label, url, onOpen, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[11px] uppercase tracking-widest text-mute font-semibold mb-1">{label}</div>
      {url ? (
        isPdf(url) ? (
          <a href={url} target="_blank" rel="noreferrer" data-testid={`${testid}-pdf`}
            className="h-24 w-32 rounded-xl border border-sand bg-elevated flex flex-col items-center justify-center gap-1 hover:ring-2 hover:ring-brand-red transition-shadow">
            <FileText className="w-8 h-8 text-brand-red" />
            <span className="text-[10px] font-bold uppercase tracking-widest">PDF</span>
            <span className="text-[10px] text-mute">Cliquer pour ouvrir</span>
          </a>
        ) : (
          <button type="button" onClick={() => onOpen({ label, url })} className="block group">
            <img src={url} alt={label} loading="lazy"
              className="h-24 w-32 object-cover rounded-xl border border-sand group-hover:ring-2 group-hover:ring-brand-red transition-shadow" />
            <span className="text-[10px] text-mute group-hover:text-brand-red">Cliquer pour agrandir</span>
          </button>
        )
      ) : (
        <span className="inline-flex items-center rounded-full bg-elevated border border-sand text-mute px-2.5 py-1 text-[11px] font-semibold">Non fourni</span>
      )}
    </div>
  );
}

/** Section admin "Documents fournis" d'un restaurant (candidature OU restaurant actif)
 * + statut "Visite terrain effectuée" + note interne (jamais visible partenaire). */
export default function RestaurantDocuments({ application, payout, verification, saveUrl, onSaved }) {
  const { t } = useTranslation();
  const [lightbox, setLightbox] = useState(null);
  const [visitDone, setVisitDone] = useState(Boolean(verification?.field_visit_done));
  const [visitDate, setVisitDate] = useState(verification?.field_visit_date || "");
  const [note, setNote] = useState(verification?.note || "");
  const [saving, setSaving] = useState(false);

  const a = application || {};
  const idLabel = a.manager_id_doc_type === "passeport" ? "Passeport" : "CNI";

  const save = async () => {
    setSaving(true);
    try {
      await apiClient.patch(saveUrl, {
        field_visit_done: visitDone,
        field_visit_date: visitDate || null,
        note: note || null,
      });
      toast.success(t("admin.rdVerifSaved"));
      onSaved && onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="restaurant-documents">
      {/* Documents images */}
      <div>
        <div className="text-xs uppercase tracking-widest text-mute font-bold mb-2">Documents fournis</div>
        {!application ? (
          <div className="text-sm text-mute rounded-xl bg-elevated border border-sand px-3 py-2.5" data-testid="docs-no-application">
            Aucune candidature liée — restaurant créé avant la mise en place des documents obligatoires.
          </div>
        ) : (
          <div className="flex flex-wrap gap-4">
            <DocThumb label={`${idLabel} responsable — recto`} url={a.manager_id_front_url} onOpen={setLightbox} testid="doc-id-front" />
            {a.manager_id_doc_type !== "passeport" && (
              <DocThumb label={`${idLabel} responsable — verso`} url={a.manager_id_back_url} onOpen={setLightbox} testid="doc-id-back" />
            )}
            <DocThumb label={t("admin.rdStorefront")} url={a.storefront_photo_url} onOpen={setLightbox} testid="doc-storefront" />
            <DocThumb label={t("admin.rdHygiene")} url={a.hygiene_certificate_url} onOpen={setLightbox} testid="doc-hygiene" />
          </div>
        )}
      </div>

      {/* Infos texte */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
        <div className="rounded-xl bg-elevated border border-sand p-3" data-testid="doc-rccm">
          <div className="text-[11px] uppercase tracking-widest text-mute font-semibold mb-1">{t("admin.rdRccm")}</div>
          {a.rccm_number ? <b>{a.rccm_number}</b>
            : <span className="inline-flex rounded-full bg-brand-yellow/40 text-[#8A6D00] px-2 py-0.5 text-[10px] font-bold uppercase">Non fourni — dossier informel</span>}
        </div>
        <div className="rounded-xl bg-elevated border border-sand p-3" data-testid="doc-payout">
          <div className="text-[11px] uppercase tracking-widest text-mute font-semibold mb-1">Compte de reversement</div>
          {payout?.number ? (
            <div>
              <b className="uppercase">{payout.operator}</b> · {payout.number}
              <div className="text-xs text-mute">Titulaire : {payout.holder_name || "—"}</div>
            </div>
          ) : <span className="text-mute">{t("admin.rdNotConfigured")}</span>}
        </div>
        <div className="rounded-xl bg-elevated border border-sand p-3" data-testid="doc-guarantor">
          <div className="text-[11px] uppercase tracking-widest text-mute font-semibold mb-1">Garant</div>
          {a.guarantor_name ? <div><b>{a.guarantor_name}</b><div className="text-xs text-mute">{a.guarantor_phone}</div></div>
            : <span className="text-mute">{t("admin.rdNotProvided")}</span>}
        </div>
      </div>

      {/* Vérification terrain + note interne */}
      <div className="rounded-xl border border-sand p-4 bg-white">
        <div className="flex items-center gap-3 flex-wrap mb-3">
          <label className="inline-flex items-center gap-2 cursor-pointer text-sm font-semibold">
            <input type="checkbox" checked={visitDone} onChange={(e) => setVisitDone(e.target.checked)}
              data-testid="field-visit-check" className="w-4 h-4 accent-[#009E60]" />
            Visite terrain effectuée
          </label>
          {visitDone && (
            <input type="date" value={visitDate} onChange={(e) => setVisitDate(e.target.value)}
              data-testid="field-visit-date"
              className="bg-white border border-sand rounded-xl px-3 py-1.5 text-sm outline-none focus:border-brand-red" />
          )}
          {verification?.updated_at && (
            <span className="text-[11px] text-mute">Dernière mise à jour : {verification.updated_at.slice(0, 10)}</span>
          )}
        </div>
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
          placeholder='Note interne admin (jamais visible par le restaurant) — ex : "Documents vérifiés, RCCM manquant mais visite terrain OK"'
          data-testid="admin-note"
          className="w-full bg-elevated border border-sand rounded-xl px-3 py-2.5 text-sm outline-none focus:border-brand-red" />
        <button onClick={save} disabled={saving} data-testid="verification-save"
          className="mt-2 inline-flex items-center gap-2 rounded-full bg-ink hover:bg-black disabled:opacity-50 text-white px-4 py-2 text-xs font-semibold">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
          Enregistrer la vérification
        </button>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-[70] bg-black/80 grid place-items-center p-4" onClick={() => setLightbox(null)} data-testid="doc-lightbox">
          <div className="max-w-4xl w-full">
            <div className="flex items-center justify-between text-white mb-2">
              <span className="font-semibold">{lightbox.label}</span>
              <button onClick={() => setLightbox(null)} data-testid="doc-lightbox-close" className="p-1.5 rounded-full bg-white/10 hover:bg-white/20"><X className="w-5 h-5" /></button>
            </div>
            <img src={lightbox.url} alt={lightbox.label} className="w-full max-h-[80vh] object-contain rounded-xl" onClick={(e) => e.stopPropagation()} />
          </div>
        </div>
      )}
    </div>
  );
}
